import { Component, Input } from '@angular/core';
import { VolatilitySurfaceData } from './volatility-surface.model';
import { ComponentVisibilityService } from '@approot/services/component-view/component-visibility.service';
import {
  MarketsService,
  Tenors
} from '@approot/services/markets/markets.service';
import { Observable, of } from 'rxjs';

const flatten = <T>(a: T[][]): T[] => [].concat.apply([], a);

@Component({
  selector: 'app-volatility-surface',
  templateUrl: './volatility-surface.component.html',
  styleUrls: ['./volatility-surface.component.scss']
})
export class VolatilitySurfaceComponent {
  @Input() currencyPairs: string[];
  @Input() widgetId;

  data: VolatilitySurfaceData;

  periods = ['1W', '2W', '1M', '2M', '3M', '6M', '9M', '1Y'];
  periodToLabel = {
    '1W': '1w',
    '2W': '2w',
    '1M': '1m',
    '2M': '2m',
    '3M': '3m',
    '6M': '6m',
    '9M': '9m',
    '1Y': '12m'
  };

  types = ['90', '75', '50', '25', '10'];
  typeToLabel = {
    90: '10P',
    75: '25P',
    50: 'ATMF',
    25: '25C',
    10: '10C'
  };

  maxValue = 0;
  minValue = 0;
  meanValue = 0;
  dataLoaded: Observable<boolean>;
  marketDataErrorMsg: string;

  _position: 'bid' | 'ask' = 'bid';
  get position() {
    return this._position;
  }
  set position(value: 'bid' | 'ask') {
    this._position = value;
    if (this.data) this.setMinMeanMaxValues(this.data);
  }

  constructor(
    private componentVisibilityService: ComponentVisibilityService,
    private marketsService: MarketsService
  ) {}

  pairToLabel(pair: string) {
    return pair.replace(/(\w{3})(\w{3})/g, '$1/$2');
  }

  onCurrencyPairChanged(value: string) {
    this.marketDataErrorMsg = '';
    this.marketsService.getLiveMarket([value]).then(p => {
      p.subscribe(resp => {
        try {
          this.data = this.multiplier(resp.fxVolSurfaces[value].tenors);
          this.setMinMeanMaxValues(this.data);
          this.dataLoaded = of(true);
        } catch (error) {
          this.dataLoaded = of(false);
          this.data = {};
          this.marketDataErrorMsg = 'No market data found.';
        }
      });
    });
  }

  closeWidget() {
    this.componentVisibilityService.removeWidget('vol', this.widgetId);
  }

  setMinMeanMaxValues(data: VolatilitySurfaceData) {
    const itemValues = Object.values(data).map(p =>
      Object.values(p).map(item => item[this.position])
    );
    const values = flatten(itemValues).sort((a: number, b: number) => a - b);
    this.maxValue = values[values.length - 1];
    this.minValue = values[0];
    this.meanValue = values.reduce((a, b) => a + b, 0) / values.length;
  }

  private multiplier(d: Tenors): VolatilitySurfaceData {
    Object.keys(d).forEach(key => {
      Object.keys(d[key]).forEach(key2 => {
        d[key][key2].bid = d[key][key2].bid * 100;
        d[key][key2].ask = d[key][key2].ask * 100;
      });
    });
    return d;
  }
}
